/** Split a typed grocery line into what to show and what to buy how much of.
 *  Dependency-free so it can be tested without the app around it.
 *
 *  A count, written before or after the item ('2 lemons', 'lemons x4') becomes
 *  '×N', off the body — 1 is never shown, since that is what having no
 *  quantity already means. A weight, volume or count-noun amount ('800 g
 *  chicken', '1 pound butter') is kept as typed, on whichever side it was
 *  written. Anything else, or a line that is only a number, is the whole
 *  body with no quantity: a quantity is a fact about an item, not a line on
 *  its own. */

// Longer spellings before their prefixes (`tablespoons` before `tbsp`
// before `t`-anything), since the alternation takes the first that matches
// and `\b` only guards the end. Package B (docs/recipe-import-plan) added
// the cooking units — cups, spoons, cloves, pinches, EL/TL — so a pasted
// recipe line and a typed `2 cups flour` split the same way.
const UNIT_ALTS = [
  'kilos', 'kilo', 'lbs', 'lb', 'oz', 'pounds', 'pound',
  'pcs', 'pc', 'packs', 'pack', 'bunches', 'bunch', 'bags', 'bag',
  'cans', 'can', 'tins', 'tin', 'jars', 'jar', 'bottles', 'bottle', 'kg', 'mg', 'ml', 'cl', 'l', 'g',
  'stück', 'stk', 'bund', 'packung', 'pck', 'dosen', 'dose', 'glas', 'flasche',
  'tablespoons', 'tablespoon', 'teaspoons', 'teaspoon', 'tbsps', 'tbsp', 'tsps', 'tsp',
  'cups', 'cup', 'cloves', 'clove', 'sticks', 'stick', 'slices', 'slice', 'pinches', 'pinch',
  'handfuls', 'handful', 'sprigs', 'sprig', 'stalks', 'stalk', 'heads', 'head', 'pints', 'pint',
  'quarts', 'quart', 'el', 'tl', 'prise', 'zehen', 'zehe', 'scheiben', 'scheibe', 'stangen', 'stange',
  'becher', 'päckchen', 'tüte', 'msp',
].join('|')

// Decimals in either `.` or `,`, a mixed number (`1 ½`, `1 1/2`), or one of
// the fraction forms — glyph or ASCII.
const FRAC = String.raw`(?:1\/2|1\/4|3\/4|1\/3|2\/3|1\/8|[½¼¾⅓⅔⅛])`
const NUM = String.raw`(?:\d+\s?${FRAC}|\d+(?:[.,]\d+)?|${FRAC})`

const TRAILING_COUNT = /^(.*?)\s*(?:[x×]\s*(\d+)|(\d+)\s*[x×])\s*$/i
const LEADING_AMOUNT = new RegExp(String.raw`^(${NUM}\s*(?:${UNIT_ALTS})\b)\s+(.+)$`, 'i')
const TRAILING_AMOUNT = new RegExp(String.raw`^(.+?)\s+(${NUM}\s*(?:${UNIT_ALTS})\b)$`, 'i')
const LEADING_COUNT_MARKER = /^(\d+)\s*[x×]\s*(.+)$/i
const LEADING_BARE_COUNT = /^(\d+)\s+(\S.*)$/

export function parseQty(raw: string): { body: string; qty: string | null } {
  const text = raw.trim()

  // Checked first: a trailing marker is unambiguous, whichever side the
  // number falls on ('lemons x4', 'lemons 4x'), and never collides with an
  // amount, since no unit word is 'x' or '×'.
  const trailing = text.match(TRAILING_COUNT)
  if (trailing && trailing[1].trim()) {
    return { body: trailing[1].trim(), qty: `×${trailing[2] ?? trailing[3]}` }
  }

  // The ambiguity rule: a leading bare number followed by a unit word is an
  // amount, so this is tried before the bare-count fallback below.
  const leadingAmount = text.match(LEADING_AMOUNT)
  if (leadingAmount && leadingAmount[2].trim()) {
    // `2 cups of flour`: the `of` belongs to the amount, not the item.
    const body = leadingAmount[2].trim().replace(/^of\s+/i, '')
    if (body) return { body, qty: leadingAmount[1].trim() }
  }

  const trailingAmount = text.match(TRAILING_AMOUNT)
  if (trailingAmount && trailingAmount[1].trim()) {
    return { body: trailingAmount[1].trim(), qty: trailingAmount[2].trim() }
  }

  const leadingMarker = text.match(LEADING_COUNT_MARKER)
  if (leadingMarker && leadingMarker[2].trim()) {
    return { body: leadingMarker[2].trim(), qty: `×${leadingMarker[1]}` }
  }

  // A leading bare number followed by anything else (not a unit, not an
  // 'x'/'×' marker) is a count — '2 lemons' means the same as 'lemons x2'.
  const leadingBare = text.match(LEADING_BARE_COUNT)
  if (leadingBare && leadingBare[2].trim()) {
    return { body: leadingBare[2].trim(), qty: `×${leadingBare[1]}` }
  }

  return { body: text, qty: null }
}

/** The inverse of `parseQty`, so an edit field can open pre-filled with one
 *  string that round-trips: a count goes after the body, an amount before it
 *  — the same side each was read from. */
export function formatLine(body: string, qty: string | null): string {
  if (!qty) return body
  return qty.startsWith('×') ? `${body} ${qty}` : `${qty} ${body}`
}

/** One typed line into the items it names. The Add field's own placeholder
 *  is `2 kg potatoes, lemons ×4`, which used to land as one item — body
 *  "2 kg potatoes, lemons", qty ×4 (UI-audit a2-13). A comma, a semicolon or
 *  a newline separates items (so a pasted shopping list works too), except a
 *  comma with a digit on both sides, which is a decimal: `1,5 kg mehl`. */
export function splitItems(raw: string): string[] {
  return raw
    .split(/[\n;]|(?<!\d),|,(?!\d)/)
    .map(s => s.trim())
    .filter(Boolean)
}

/** The key two grocery lines are "the same item" under: case and spacing
 *  only. No stemming — `lemon` and `lemons` stay two things rather than a
 *  guess merging `pasta` into `pastas`. */
export const itemKey = (body: string): string => body.trim().replace(/\s+/g, ' ').toLocaleLowerCase('de')

/** A count as a number: no quantity is one, `×N` is N, and an amount
 *  (`250 g`) is not a count at all. */
function countOf(qty: string | null): number | null {
  if (qty == null) return 1
  const m = qty.match(/^×(\d+)$/)
  return m ? Number(m[1]) : null
}

/** Two counts of the same item, added. An amount on either side cannot be
 *  added to anything without knowing its unit, so that answers `undefined`
 *  — the caller leaves the item as it is and says it is already there. */
export function addCounts(a: string | null, b: string | null): string | undefined {
  const x = countOf(a), y = countOf(b)
  if (x == null || y == null) return undefined
  return `×${x + y}`
}

export interface GroceryAddPlan {
  /** New rows, in the order typed. */
  adds: { body: string; qty: string | null }[]
  /** Open rows the line named again, with the count they now carry. */
  bumps: { id: string; body: string; qty: string }[]
  /** Open rows named again whose quantity is an amount, left untouched. */
  already: { id: string; body: string }[]
}

/**
 * What an Add on the grocery list does. Re-typing something already open on
 * the list used to add a second row — three "emmentaler" (UI-audit a2-12) —
 * so a piece that names an open item bumps that item's count instead, and a
 * piece repeated within the same line is counted once with both quantities.
 *
 * An open item whose quantity is an amount cannot be added to, so it lands
 * in `already` — "it is on the list", without a write. Pure, so
 * `test/list-model.test.ts` runs it.
 */
export function planGroceryAdd(
  text: string,
  open: { id: string; body: string; qty: string | null }[],
): GroceryAddPlan {
  const adds: GroceryAddPlan['adds'] = []
  const bumps = new Map<string, { id: string; body: string; qty: string }>()
  const already = new Map<string, { id: string; body: string }>()
  const openByKey = new Map<string, { id: string; body: string; qty: string | null }>()
  for (const o of open) if (!openByKey.has(itemKey(o.body))) openByKey.set(itemKey(o.body), o)

  for (const piece of splitItems(text)) {
    const { body, qty } = parseQty(piece)
    if (!body) continue
    const key = itemKey(body)

    const existing = openByKey.get(key)
    if (existing) {
      if (already.has(existing.id)) continue
      const current = bumps.get(existing.id)?.qty ?? existing.qty
      const next = addCounts(current, qty)
      if (next === undefined) {
        if (!bumps.has(existing.id)) already.set(existing.id, { id: existing.id, body: existing.body })
        continue
      }
      bumps.set(existing.id, { id: existing.id, body: existing.body, qty: next })
      continue
    }

    const earlier = adds.find(a => itemKey(a.body) === key)
    if (earlier) {
      const next = addCounts(earlier.qty, qty)
      if (next !== undefined) earlier.qty = next
      continue
    }
    adds.push({ body, qty })
  }

  return { adds, bumps: [...bumps.values()], already: [...already.values()] }
}
